import { DataTable, DataTableSortStatus } from 'mantine-datatable';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  ActionIcon,
  Button,
  Group,
  Select,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { openModal } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import {
  IconFileExcel,
  IconInfoCircle,
  IconPencil,
  IconPlus,
  IconSearch,
  IconX,
} from '@tabler/icons-react';

import {
  Contact,
  ContactsSortBy,
  GetContactsRequestParams,
} from '@interfaces/contact.interface';

import {
  useCountContactsQuery,
  useGetManyContactsQuery,
  useLazyGetContactsXlsxQuery,
} from '@api/contacts.api';
import { isApiError } from '@api/index';

import useLayoutProps from '@hooks/useLayoutProps';
import useStateWithDebounce from '@hooks/useStateWithDebounce';

import UpsertContact, {
  UPSERT_CONTACT_MODAL_ID,
} from '@components/UpsertContact';

import ContactDetail, { DETAIL_CONTACT_MODAL_ID } from './ContactDetail';

export default function ContactList() {
  // ==========================================================================
  // General
  // ==========================================================================
  const scrollViewportRef = useRef<HTMLDivElement>(null);
  const [filters, setFilters] = useState<GetContactsRequestParams>({
    page: +1,
    pageLength: +10,
    type: undefined,
    sortBy: 'firstName',
    sortOrder: 'asc',
  });
  const [selectedRecords, setSelectedRecords] = useState<Contact[]>([]);

  const [firstNameSearch, setFirstNameSearch, firstNameDebounced] =
    useStateWithDebounce('');
  const [lastNameSearch, setLastNameSearch, lastNameDebounced] =
    useStateWithDebounce('');
  const [phoneSearch, setPhoneSearch, phoneDebounced] =
    useStateWithDebounce('');
  const [sortStatus, setSortStatus] = useState<DataTableSortStatus<Contact>>({
    columnAccessor: 'firstName',
    direction: 'asc',
  });
  // ==========================================================================
  // State
  // ==========================================================================
  useEffect(() => {
    // Skip the initial mount
    if (
      filters.sortBy === sortStatus.columnAccessor &&
      filters.sortOrder === sortStatus.direction
    ) {
      return;
    }

    setFilters((filters) => ({
      ...filters,
      sortBy: sortStatus.columnAccessor as ContactsSortBy,
      sortOrder: sortStatus.direction,
    }));
  }, [sortStatus]);

  // ==========================================================================
  // Api
  // ==========================================================================
  const { data: contacts = [], isLoading: loading } = useGetManyContactsQuery({
    ...filters,
    firstNameSearch: firstNameDebounced,
    lastNameSearch: lastNameDebounced,
    phoneSearch: phoneDebounced,
  });

  const { data: contactsCount = { count: 0 }, isLoading: isLoadingCount } =
    useCountContactsQuery({
      firstNameSearch: firstNameDebounced,
      lastNameSearch: lastNameDebounced,
      phoneSearch: phoneDebounced,
    });

  const [getContactsXlsx, { isLoading: isLoadingXlsx }] =
    useLazyGetContactsXlsxQuery();

  // ==========================================================================
  // Form
  // ==========================================================================

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const handleDetailContactClick = useCallback((contact: Contact) => {
    openModal({
      modalId: DETAIL_CONTACT_MODAL_ID,
      title: `Contatto ${contact.firstName} ${contact.lastName} (${contact.type === 'company' ? 'Azienda' : 'Privato'})`,
      children: <ContactDetail data={contact} />,
      size: 'lg',
    });
  }, []);

  const handleUpsertContactClick = useCallback((contact?: Contact) => {
    openModal({
      modalId: UPSERT_CONTACT_MODAL_ID,
      title: contact ? 'Modifica contatto' : 'Aggiungi contatto',
      children: <UpsertContact contact={contact} />,
      size: 'md',
    });
  }, []);

  const handleExportContactClick = async () => {
    try {
      const blob = await getContactsXlsx({
        ids: selectedRecords.map((c) => c.id),
        type: filters.type!,
      }).unwrap();

      const alink = document.createElement('a');
      alink.href = window.URL.createObjectURL(blob);
      alink.download =
        filters.type === 'company' ? 'IEIS Aziende.xlsx' : 'IEIS Privati.xlsx';
      alink.click();
      alink.remove();
    } catch (e) {
      if (isApiError(e)) {
        showNotification({
          title: 'Errore',
          message: e.data.message,
          color: 'red',
        });
      } else {
        showNotification({
          title: 'Errore inatteso',
          message: 'Si è verificato un errore.',
          color: 'red',
        });
      }
    }
  };

  // ==========================================================================
  // Render
  // ==========================================================================
  const title = useMemo(
    () => `Contatti ${!isLoadingCount ? `(${contactsCount.count})` : ''}`,
    [contactsCount.count, isLoadingCount],
  );

  useLayoutProps({
    title,
  });

  return (
    <>
      <Group mb="md" justify="end" align="center">
        <Button
          leftSection={<IconPlus />}
          onClick={() => {
            handleUpsertContactClick();
          }}
        >
          Aggiungi
        </Button>
        {selectedRecords.length === 0 || !filters.type ? (
          <Tooltip label="Attiva il filtro Tipologia e seleziona almeno un contatto">
            <Button
              loading={isLoadingXlsx}
              color="green"
              leftSection={<IconFileExcel />}
              onClick={handleExportContactClick}
              disabled
            >
              Esporta
            </Button>
          </Tooltip>
        ) : (
          <Button
            loading={isLoadingXlsx}
            color="green"
            leftSection={<IconFileExcel />}
            onClick={handleExportContactClick}
          >
            Esporta
          </Button>
        )}
      </Group>

      <DataTable
        withTableBorder
        borderRadius="sm"
        striped
        noRecordsText="Nessuna contatto trovato"
        minHeight={contacts.length === 0 ? 200 : undefined}
        columns={[
          {
            accessor: 'firstName',
            title: 'Nome',
            sortable: true,
            filter: (
              <TextInput
                placeholder="Ricerca nome"
                leftSection={<IconSearch size={16} />}
                rightSection={
                  firstNameSearch.trim() !== '' ? (
                    <ActionIcon
                      size="sm"
                      variant="transparent"
                      c="dimmed"
                      onClick={() => setFirstNameSearch('')}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  ) : undefined
                }
                value={firstNameSearch}
                onChange={(e) => setFirstNameSearch(e.currentTarget.value)}
              />
            ),
          },
          {
            accessor: 'lastName',
            title: 'Cognome',
            sortable: true,
            filter: (
              <TextInput
                placeholder="Ricerca per cognome"
                leftSection={<IconSearch size={16} />}
                rightSection={
                  lastNameSearch.trim() !== '' ? (
                    <ActionIcon
                      size="sm"
                      variant="transparent"
                      c="dimmed"
                      onClick={() => setLastNameSearch('')}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  ) : undefined
                }
                value={lastNameSearch}
                onChange={(e) => setLastNameSearch(e.currentTarget.value)}
              />
            ),
          },
          {
            accessor: 'phone',
            title: 'Telefono',
            filter: (
              <TextInput
                placeholder="Ricerca per telefono"
                leftSection={<IconSearch size={16} />}
                rightSection={
                  phoneSearch.trim() !== '' ? (
                    <ActionIcon
                      size="sm"
                      variant="transparent"
                      c="dimmed"
                      onClick={() => setPhoneSearch('')}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  ) : undefined
                }
                value={phoneSearch}
                onChange={(e) => setPhoneSearch(e.currentTarget.value)}
              />
            ),
          },
          {
            accessor: 'type',
            title: 'Tipologia',
            render: (value) => {
              return value.type === 'company' ? 'Azienda' : 'Privato';
            },
            filter: (
              <Select
                label="Filtra per tipologia"
                placeholder="Seleziona tipo"
                data={[
                  { value: 'company', label: 'Aziende' },
                  { value: 'private', label: 'Privati' },
                ]}
                value={filters.type || ''}
                onChange={(value) => {
                  setFilters((prev) => ({
                    ...prev,
                    type: value ? (value as Contact['type']) : undefined,
                    page: 1,
                  }));
                  setSelectedRecords([]);
                }}
                clearable
              />
            ),
          },
          {
            accessor: 'actions',
            title: '',
            textAlign: 'right',
            width: '0%',
            render: (value) => {
              return (
                <Group wrap="nowrap" gap="xs">
                  <Tooltip label="Modifica contatto">
                    <ActionIcon
                      variant="subtle"
                      onClick={() => handleUpsertContactClick(value)}
                    >
                      <IconPencil />
                    </ActionIcon>
                  </Tooltip>{' '}
                  <Tooltip label="Visualizzi dettagli">
                    <ActionIcon
                      variant="subtle"
                      onClick={() => handleDetailContactClick(value)}
                    >
                      <IconInfoCircle />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              );
            },
          },
        ]}
        records={contacts}
        fetching={loading || isLoadingCount}
        scrollViewportRef={scrollViewportRef}
        /* Sorting */
        sortStatus={sortStatus}
        onSortStatusChange={setSortStatus}
        /* Seelction */

        selectedRecords={filters.type ? selectedRecords : undefined}
        onSelectedRecordsChange={filters.type ? setSelectedRecords : undefined}
        /* Pagination */
        page={filters.page!}
        recordsPerPage={filters.pageLength!}
        totalRecords={contactsCount.count}
        paginationText={() => ''}
        recordsPerPageLabel=""
        recordsPerPageOptions={[10, 20, 50]}
        onPageChange={(newPage) => setFilters({ ...filters, page: newPage })}
        onRecordsPerPageChange={(newPageLength) =>
          setFilters({ ...filters, pageLength: newPageLength, page: 1 })
        }
      />
    </>
  );
}
